import { Fragment } from "react";
import { Link } from "react-router";
import dayjs from "dayjs";
import { formatMoney } from "@/lib/format";
import { STATUS_META, type ContractingUserStatus } from "@/lib/domain-constants";
import { cn } from "@/lib/utils";
import type { SuretyPendingGuarantee } from "@/types/surety";
import { EmptyNote } from "./SuretyUI";
import { AddToTeamButton } from "./SuretyActions";

/**
 * Clientes que ganaron y todavía no firman: necesitan su garantía en días. Va como
 * lista y no como tabla: la tarjeta ocupa dos tercios de la pantalla y con cinco
 * columnas cada dato se partía en dos líneas.
 */
export function PendingGuaranteesTable({ rows }: { rows: SuretyPendingGuarantee[] }) {
  if (rows.length === 0) {
    return (
      <EmptyNote>
        Ningún cliente ganó en los últimos 30 días sin firmar todavía su contrato.
      </EmptyNote>
    );
  }

  return (
    <div className="flex flex-col divide-y">
      {rows.map((row) => {
        const status =
          row.pipelineStatus !== null
            ? STATUS_META[row.pipelineStatus as ContractingUserStatus]
            : null;
        const days = dayjs().diff(dayjs(row.awardedAt), "day");
        return (
          <article
            key={row.contractingId}
            className="grid gap-3 py-4 first:pt-0 last:pb-0 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center"
          >
            <div className="min-w-0">
              <Link
                to={`/contracting/${row.contractingId}`}
                className="cursor-pointer font-bold text-slate-900 hover:text-primary dark:text-slate-50"
              >
                {row.winnerName || row.clientName}
              </Link>
              <p className="truncate text-xs text-slate-500">
                {[row.nomenclature, row.entity, row.department].filter(Boolean).join(" · ")}
              </p>
              <p className="text-xs text-slate-500">
                Buena pro el {dayjs(row.awardedAt).format("DD MMM")}
                {/* Pasados 5 días la garantía ya apura: se marca en ámbar, no en rojo. */}
                <span className={cn(days >= 5 && "font-semibold text-amber-700 dark:text-amber-400")}>
                  {" "}· hace {days} {days === 1 ? "día" : "días"}
                </span>
                {" "}· adjudicado {formatMoney(row.amount)}
              </p>
              {row.members.length > 0 && (
                <p className="mt-1 text-xs text-slate-500">
                  Integrantes:{" "}
                  {row.members.map((member, index) => (
                    <Fragment key={member.companyId}>
                      {index > 0 && " · "}
                      <span
                        className={cn(
                          member.isClient
                            ? "font-semibold text-primary"
                            : "text-slate-600 dark:text-slate-300",
                        )}
                      >
                        {member.name}
                        {member.isClient && " (tu cliente)"}
                      </span>
                    </Fragment>
                  ))}
                </p>
              )}
            </div>

            <div className="flex items-center gap-4 sm:justify-end">
              <div className="text-right">
                <p className="text-[11px] text-slate-500">Fianza est.</p>
                <p className="whitespace-nowrap text-base font-extrabold text-primary">
                  {formatMoney(row.guaranteeEstimate)}
                </p>
              </div>
              {/* Fuera de Mi equipo, el botón; adentro, su estado y quién la lleva. */}
              <div className="flex w-48 shrink-0 flex-col items-end gap-1 whitespace-nowrap">
                {status ? (
                  <>
                    <span className={cn("rounded-full px-2.5 py-1 text-xs font-bold", status.badge)}>
                      {status.label}
                    </span>
                    {row.assigneeName ? (
                      <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">
                        {row.assigneeName}
                      </span>
                    ) : (
                      <span className="text-xs font-bold text-red-600">Sin responsable</span>
                    )}
                  </>
                ) : (
                  <AddToTeamButton contractingId={row.contractingId} inPipeline={false} />
                )}
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}
